import React from "react";
import { Helmet } from "react-helmet-async";
import {
  SITE_NAME,
  SITE_URL,
  DEFAULT_OG_IMAGE,
  TWITTER_HANDLE,
  OG_LOCALE,
  canonicalUrl,
} from "@/lib/seo";

/**
 * SEO meta tags for any page. Supports:
 *  - title / description / canonical
 *  - OG + Twitter cards
 *  - hreflang alternates (pass array of { lang, href })
 *  - JSON-LD structured data via `schema` prop
 *  - article meta (publishedTime, modifiedTime, author, section)
 */
export default function SEO({
  title,
  description,
  path = "/",
  lang = "ca",
  image,
  type = "website",
  alternates = [],
  schema = null,
  article = null,
  noindex = false,
}) {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : SITE_NAME;
  const url = canonicalUrl(path);
  const ogImage = image || DEFAULT_OG_IMAGE;
  const ogLocale = OG_LOCALE[lang] || "ca_ES";

  return (
    <Helmet prioritizeSeoTags>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description || ""} />
      <link rel="canonical" href={url} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {alternates.map((alt) => (
        <link
          key={alt.lang}
          rel="alternate"
          hrefLang={alt.lang}
          href={canonicalUrl(alt.path)}
        />
      ))}
      {alternates.length > 0 && (
        <link rel="alternate" hrefLang="x-default" href={SITE_URL} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || ""} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description || ""} />
      <meta name="twitter:image" content={ogImage} />

      {/* Article-specific */}
      {article && article.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article && article.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {article && article.author && (
        <meta property="article:author" content={article.author} />
      )}
      {article && article.section && (
        <meta property="article:section" content={article.section} />
      )}

      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
}
